import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchMicroAppBindingData, type MicroAppBindingData } from "@/api";
import { extractBindingKeys } from "./hydrationRuntime";

const READY = "micro-app:ready";
const HYDRATED = "micro-app:hydrated";
const HYDRATE = "micro-app:hydrate";
const OPEN_LINK = "micro-app:open-link";

/** Why a binding couldn't refresh (deduplicated across bindings). */
export interface RefreshProblem {
  code: string;
  source: string | null;
}

/** The distinct reasons bindings failed, by code + source. */
export function refreshProblems(bindings: MicroAppBindingData[]): RefreshProblem[] {
  const seen = new Map<string, RefreshProblem>();
  for (const b of bindings) {
    if (b.error == null) continue;
    const problem = { code: b.errorCode ?? "refresh_failed", source: b.errorSource ?? null };
    seen.set(`${problem.code}|${problem.source ?? ""}`, problem);
  }
  return [...seen.values()];
}

export interface DashboardHydrationState {
  /** Whether this app has any live bindings (drives the "Live" badge). */
  isLive: boolean;
  /** When the data shown was fetched (the oldest binding's, as values can come from the cache), or null before the first hydrate. */
  lastUpdated: Date | null;
  /** True while binding data is being fetched. */
  refreshing: boolean;
  /** Number of bindings whose last fetch failed (served stale/last-known). */
  errorCount: number;
  /** Why they failed (distinct reasons). */
  problems: RefreshProblem[];
  /** Fetch all bindings now (skipping the cache) and push them into the iframe; resolves to the reasons any failed. */
  refresh: () => Promise<RefreshProblem[]>;
}

/**
 * Drives live-data hydration for a dashboard micro app. Ported from the old
 * UI's useDashboardHydration, adapted to the new src/api seam.
 *
 * The iframe is opaque-origin, so the parent cannot reach into it. Instead:
 *  1. Parent parses the bindings manifest to learn which keys exist.
 *  2. Parent fetches each binding's data (with the user's token).
 *  3. The injected runtime posts `micro-app:ready` when it can receive data.
 *  4. Parent posts `{ type: "micro-app:hydrate", bindings }` into the iframe.
 *  5. Runtime hydrates the cells and posts `micro-app:hydrated` back.
 */
export function useDashboardHydration({
  uuid,
  html,
  iframeRef,
}: {
  uuid: string | undefined;
  html: string;
  iframeRef: RefObject<HTMLIFrameElement | null>;
}): DashboardHydrationState {
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [problems, setProblems] = useState<RefreshProblem[]>([]);
  const bindingKeys = html ? extractBindingKeys(html) : [];
  const isLive = bindingKeys.length > 0;

  // Latest fetched payload, kept so we can (re)post whenever the iframe runtime
  // announces readiness (it re-announces until it receives data).
  const payloadRef = useRef<Record<string, MicroAppBindingData> | null>(null);
  const keysRef = useRef<string[]>(bindingKeys);
  keysRef.current = bindingKeys;
  // Monotonic token identifying the current fetch generation; a resolved fetch
  // whose token no longer matches is stale (superseded) and dropped.
  const requestTokenRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const postToIframe = useCallback(
    (bindings: Record<string, MicroAppBindingData>) => {
      const win = iframeRef.current?.contentWindow;
      if (win) win.postMessage({ type: HYDRATE, bindings }, "*");
    },
    [iframeRef],
  );

  const load = useCallback(
    async (force: boolean): Promise<RefreshProblem[]> => {
      if (!uuid || keysRef.current.length === 0) return [];
      const token = ++requestTokenRef.current;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setRefreshing(true);
      try {
        const results = await Promise.all(
          keysRef.current.map(async (key): Promise<[string, MicroAppBindingData]> => {
            try {
              const data = await fetchMicroAppBindingData(uuid, key, controller.signal, force);
              return [key, data];
            } catch {
              return [
                key,
                {
                  bindingKey: key,
                  value: null,
                  executedAt: null,
                  cached: false,
                  error: "load_failed",
                  errorCode: "refresh_failed",
                },
              ];
            }
          }),
        );
        if (token !== requestTokenRef.current || !mountedRef.current) return [];
        const map = Object.fromEntries(results) as Record<string, MicroAppBindingData>;
        payloadRef.current = map;
        const found = refreshProblems(Object.values(map));
        setErrorCount(Object.values(map).filter((b) => b.error != null).length);
        setProblems(found);
        postToIframe(map);
        return found;
      } finally {
        if (token === requestTokenRef.current && mountedRef.current) setRefreshing(false);
      }
    },
    [uuid, postToIframe],
  );

  // Listen for the iframe runtime's lifecycle messages.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      // srcDoc iframes have an opaque origin ("null"); reject anything else.
      if (event.origin !== "null") return;
      const iframeWin = iframeRef.current?.contentWindow;
      if (!iframeWin || event.source !== iframeWin) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.type === READY) {
        if (payloadRef.current) postToIframe(payloadRef.current);
      } else if (data.type === HYDRATED) {
        setLastUpdated(fetchedAt(payloadRef.current));
      } else if (data.type === OPEN_LINK && isOpenableLink(data.url)) {
        // A link in an app's table (e.g. a HubSpot record). The sandboxed iframe can't open windows.
        window.open(data.url, "_blank", "noopener,noreferrer");
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [iframeRef, postToIframe]);

  // Clear buffered state and invalidate any in-flight fetch on a fresh document.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset runs only on a fresh document (html).
  useEffect(() => {
    requestTokenRef.current++;
    abortRef.current?.abort();
    payloadRef.current = null;
    setErrorCount(0);
    setProblems([]);
  }, [html]);

  // Kick off the initial fetch once we know the app is live.
  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh identity is stable; we refetch on app/doc/liveness change.
  useEffect(() => {
    // Opening the app shows cached values while they're fresh; Refresh data fetches now.
    if (isLive) void load(false);
  }, [uuid, isLive, html, load]);

  const refresh = useCallback(() => load(true), [load]);
  return { isLive, lastUpdated, refreshing, errorCount, problems, refresh };
}

/** The oldest fetch time among the bindings shown (now, if none says). */
function fetchedAt(payload: Record<string, MicroAppBindingData> | null): Date {
  const times = Object.values(payload ?? {})
    .map((b) => (b.executedAt ? Date.parse(b.executedAt) : Number.NaN))
    .filter((t) => !Number.isNaN(t));
  return times.length > 0 ? new Date(Math.min(...times)) : new Date();
}

/** Links an app may ask the viewer to open: https only (no javascript:, data: or http:). */
export function isOpenableLink(url: unknown): url is string {
  return typeof url === "string" && /^https:\/\/[^\s]+$/i.test(url);
}
